/**
 * useDashboardLayout - the signed-in person's Dashboard layout, saved on the
 * server (GET / PUT / DELETE /api/dashboard-layout) so it follows them to
 * any device. The last one read is also kept in this browser, so the
 * Dashboard draws in its own layout straight away instead of jumping from
 * the default once the server answers (a convenience only: the server's is
 * the one that counts, and anything unreadable is the default).
 *
 *   const { layout, save } = useDashboardLayout(email);
 *   await save(next);      // throws when the server refuses; a layout equal
 *                          // to the default is a DELETE (it then follows any
 *                          // later change to the default)
 */
import { useCallback, useEffect, useState } from 'react';
import { defaultLayout, normalizeLayout, sameLayout } from './dashboardLayout';

const API = '/api/dashboard-layout';
const cacheKey = (email) => (email ? `globalops_dash_layout:${String(email).toLowerCase()}` : null);

function readCache(key) {
  if (!key) return null;
  try {
    return JSON.parse(window.localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}
function writeCache(key, layout) {
  if (!key) return;
  try {
    if (layout) window.localStorage.setItem(key, JSON.stringify(layout));
    else window.localStorage.removeItem(key);
  } catch {
    /* private window / blocked storage: the server still has it */
  }
}

export function useDashboardLayout(email) {
  const key = cacheKey(email);
  const [layout, setLayout] = useState(() => normalizeLayout(readCache(key)));

  useEffect(() => {
    let off = false;
    // a fetch that cannot even start (no network layer) leaves what we have
    Promise.resolve()
      .then(() => fetch(API))
      .then((r) => (r && r.ok ? r.json() : null))
      .then((body) => {
        if (off || !body || typeof body !== 'object' || Array.isArray(body)) return;
        setLayout(normalizeLayout(body.layout));
        writeCache(key, body.layout || null);
      })
      .catch(() => {});
    return () => {
      off = true;
    };
  }, [key]);

  const save = useCallback(
    async (next) => {
      const isDefault = sameLayout(next, defaultLayout());
      const r = await fetch(
        API,
        isDefault
          ? { method: 'DELETE' }
          : { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ layout: next }) }
      );
      if (!r || !r.ok) throw new Error('Could not save the layout');
      const body = await r.json().catch(() => null);
      const stored = isDefault ? null : (body && body.layout) || next;
      setLayout(normalizeLayout(stored));
      writeCache(key, stored);
    },
    [key]
  );

  return { layout, save };
}

export default useDashboardLayout;
